import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Users, MapPinned } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, errMsg, imgSrc } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ImageUpload } from "@/components/ImageUpload";

export const GroupFormDialog = ({ open, setOpen, initial, onSaved }) => {
  const [f, setF] = useState(initial || { name: "", description: "", cover_url: "" });
  useEffect(() => {
    if (open) setF(initial || { name: "", description: "", cover_url: "" });
  }, [open, initial]);
  const save = async (e) => {
    e.preventDefault();
    try {
      const { data } = initial?.id ? await api.put(`/groups/${initial.id}`, f) : await api.post("/groups", f);
      onSaved(data);
      setOpen(false);
    } catch (err) {
      toast.error(errMsg(err));
    }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="bg-white" data-testid="group-form-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl">{initial?.id ? "Edit group" : "New group"}</DialogTitle></DialogHeader>
        <form onSubmit={save} className="space-y-3">
          <input className="hb-input" placeholder="Group name, e.g. San Diego (SD)" required value={f.name}
            onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="group-name-input" />
          <textarea className="hb-input" placeholder="Who is this group for?" value={f.description}
            onChange={(e) => setF({ ...f, description: e.target.value })} data-testid="group-description-input" />
          <div className="flex items-center gap-3">
            {f.cover_url && <img src={imgSrc(f.cover_url)} alt="" className="w-28 aspect-video object-cover rounded-lg" />}
            <ImageUpload testId="group-cover-upload" label={f.cover_url ? "Change cover" : "Upload cover (optional)"}
              onUploaded={(url) => setF((prev) => ({ ...prev, cover_url: url }))} />
          </div>
          <button className="btn-primary w-full" data-testid="group-save-button">Save group</button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const GroupCard = ({ g, i }) => (
  <Link to={`/groups/${g.id}`} className="hb-card overflow-hidden rise group hover:border-[var(--hb-ink)] transition-colors"
    style={{ animationDelay: `${i * 50}ms` }} data-testid={`group-card-${g.id}`}>
    <div className="relative h-32 bg-[var(--hb-ink)] overflow-hidden grain">
      {g.cover_url ? (
        <img src={imgSrc(g.cover_url)} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
      ) : (
        <div className="absolute inset-0 grid place-items-center text-[var(--hb-lime)]"><MapPinned size={40} /></div>
      )}
      {g.joined && <span className="absolute top-3 right-3 label-accent !text-[10px] bg-[var(--hb-lime)] !text-[var(--hb-ink)] px-2 py-1 rounded-full">Joined</span>}
    </div>
    <div className="p-5">
      <h3 className="font-display text-xl">{g.name}</h3>
      {g.description && <p className="text-sm text-[var(--hb-muted)] mt-1 line-clamp-2">{g.description}</p>}
      <div className="flex items-center gap-1.5 mt-3 text-xs text-[var(--hb-muted)]" data-testid={`group-member-count-${g.id}`}>
        <Users size={13} /> {g.member_count} member{g.member_count === 1 ? "" : "s"}
      </div>
    </div>
  </Link>
);

export default function GroupsPage() {
  const { user } = useAuth();
  const [groups, setGroups] = useState(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    api.get("/groups").then((r) => setGroups(r.data));
  }, []);

  return (
    <div data-testid="groups-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="label-accent">Find your city</div>
          <h1 className="font-display text-4xl sm:text-5xl mt-1">Groups</h1>
          <p className="text-sm text-[var(--hb-muted)] mt-2">Join your local group to see discussions for your area.</p>
        </div>
        {user.role === "admin" && (
          <button className="btn-primary" onClick={() => setOpen(true)} data-testid="groups-new-button"><Plus size={14} /> New group</button>
        )}
      </div>
      {groups?.length === 0 && <div className="hb-card p-10 text-center mt-8 text-[var(--hb-muted)]" data-testid="groups-empty">No groups yet.</div>}
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-8">
        {groups?.map((g, i) => <GroupCard key={g.id} g={g} i={i} />)}
      </div>
      <GroupFormDialog open={open} setOpen={setOpen} onSaved={(g) => setGroups([...(groups || []), g].sort((a, b) => a.name.localeCompare(b.name)))} />
    </div>
  );
}
